'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import api, { getGatewayErrorCode } from '@/api/client'
import type { ModelsClub } from '@/api/generated-api'
import { ModerationCards } from '@/app/[locale]/clubs/moderation/components/ModerationCards'
import { SupportModal } from '@/app/[locale]/clubs/moderation/components/SupportModal'
import { SupportTicketsList } from '@/app/[locale]/clubs/moderation/components/SupportTicketsList'
import { hasActiveSupportTicket } from '@/lib/supportTicketStatus'
import { showToast } from '@/providers/ToastProvider/ToastMessage'

import S from '../../../moderation/ClubModeration.module.scss'
import { BlockedClubBanner } from './BlockedClubBanner'
import { BlockedClubProfileHeader } from './BlockedClubProfileHeader'

type BlockedClubProfileViewProps = {
  club: ModelsClub
}

const BlockedClubProfileView = ({ club }: BlockedClubProfileViewProps) => {
  const tSupport = useTranslations('clubs.moderation')
  const queryClient = useQueryClient()
  const [supportModalOpen, setSupportModalOpen] = useState(false)
  const [supportMessage, setSupportMessage] = useState('')
  const [supportSubmitted, setSupportSubmitted] = useState(false)

  const supportTicketMutation = useMutation({
    mutationFn: (message: string) =>
      api.v2ClubsSupportTicketsCreate({ message }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['clubs', 'support-tickets', 'me']
      })
      setSupportMessage('')
      setSupportSubmitted(true)
    },
    onError: (error: unknown) => {
      const code = getGatewayErrorCode(error)
      const titleKey =
        code === 'SP-200'
          ? 'supportModal.alreadyOpenError'
          : 'supportModal.error'
      showToast({ type: 'error', title: tSupport(titleKey) })
    }
  })

  const { data: supportTickets } = useQuery({
    queryKey: ['clubs', 'support-tickets', 'me'],
    queryFn: () => api.v2ClubsSupportTicketsMeList()
  })

  const isSupportTicketPending = useMemo(
    () => hasActiveSupportTicket(supportTickets),
    [supportTickets]
  )

  const clubName = club.club_name?.trim() || 'Club'

  return (
    <div className={S.page}>
      <div className={S.container}>
        <BlockedClubProfileHeader clubName={clubName} />

        <BlockedClubBanner
          onContactSupport={() => {
            setSupportSubmitted(false)
            setSupportModalOpen(true)
          }}
          hasActiveSupportTicket={isSupportTicketPending}
        />

        <ModerationCards />

        <SupportTicketsList />
      </div>

      <SupportModal
        open={supportModalOpen}
        onClose={() => {
          setSupportModalOpen(false)
          setSupportSubmitted(false)
        }}
        message={supportMessage}
        onMessageChange={setSupportMessage}
        onSubmit={msg => supportTicketMutation.mutate(msg)}
        isPending={supportTicketMutation.isPending}
        submitted={supportSubmitted}
      />
    </div>
  )
}

export default BlockedClubProfileView
